import { action, makeAutoObservable, observable } from "mobx";
import { TIMELINE_MIN_DURATION } from "../config/config";
import {CompositionService} from "../services/CompositionService";

export enum MediaDataStatusEnum {
    LOADING = "loading",
    READY = "ready",
}

export interface ClipFilter {
    id: string;
    sourceId: string;
}

export type ClipEffect = ClipFilter;

export interface Clip {
    id: string;
    type: string;
    startTime: number;
    leftTrim: number;
    rightTrim: number;
    duration: number;
    trimmedDuration: number;
    mediaDataId?: string;
    text?: string;
    hasPredecessor?: boolean;
    hasSuccessor?: boolean;
}

export interface Transition {
    id: string;
    startClipId: string;
    endClipId: string;
    inDuration: number;
    outDuration: number;
}

export interface Layer {
    id: string;
    clipsIds: string[];
    transitionIds: string[];
}

export interface Display {
    width: number;
    height: number;
    backgroundColor: string;
}

export interface MediaData {
    id: string;
    thumbnail?: string;
    type?: string;
    filename?: string;
    status: MediaDataStatusEnum;
    duration?: number;
}

export interface WaveformData {
    id: string;
    duration?: number;
    data: Float32Array;
}

export interface Render {
    blobUrl?: string;
    error?: string;
    extension?: string;
}

export interface SubtitlesStyles {
    selectedMode: string | null;
    selectedAnimation: string | null;
    selectedStyle: string | null;
}

class CompositionStoreInstance {
    isPlaying: boolean = false;
    currentTime: number = 0;
    duration: number = TIMELINE_MIN_DURATION;
    display: Display = {
        width: 1920,
        height: 1080,
        backgroundColor: "#000000",
    };
    scriptLayerId: string;

    media: Record<string, MediaData> = {};
    waveform: Record<string, WaveformData> = {};
    librettoClipToRendleyClips: Record<string, Set<string>> = {}; // librettoClipId to [rendley Clips in the LibrettoClip] mapping
    librettoClipToLayer: Record<string, string> = {}; // librettoClipId -> layerId of layer the clip is on mapping
    layerToLibrettoClips: Record<string, Set<string>> = {}; // layerId -> all LibrettoClipIds on that layer mapping

    clips: Record<string, Clip> = {};
    layers: Record<string, Layer> = {};
    transitions: Record<string, Transition> = {};
    layersOrder: string[] = [];
    styles: Record<string, any> = {};
    updateTimestamp: number = 0;
    render: Render = {
        blobUrl: undefined,
        error: undefined,
        extension: "mp4",
    };
    subtitlesStyles: SubtitlesStyles = {
        selectedMode: "full",
        selectedAnimation: null,
        selectedStyle: null,
    };
    // subtitlesMainTextStyle: Partial<MainTextStyle> = {};
    // subtitlesHighlightStyle: Partial<HighlightTextStyle> = {};
    canUndo: boolean = false;
    canRedo: boolean = false;

    constructor() {
        makeAutoObservable(this, {
            setDuration: action,
            updateTimelineDuration: action,
            resetRender: action,
            updateRender: action,
            setDisplayResolution: action,
            setDisplayBackgroundColor: action,
            setIsPlaying: action,
            setCurrentTime: action,
            setScriptLayerId: action,
            addLayer: action,
            deleteLayer: action,
            setClip: action,
            setClips: action,
            updateClip: action,
            updateLayer: action,
            updateStyles: action,
            setStyles: action,
            addClip: action,
            addMedia: action,
            deleteMedia: action,
            setLayersOrder: action,
            addTransition: action,
            setTransitions: action,
            deleteTransition: action,
            setUpdateTimestamp: action,
            getLayerFromClipId: observable,
            getLayerFromTransitionId: observable,
            getFilenameByClipId: observable,
            getTextByClipId: observable,
            getThumbnailByClipId: observable,
            // getSubtitlesHighlightStyle: observable,
            // getSubtitlesMainTextStyle: observable,
            setUndoRedoState: action,
            // setSubtitlesMainTextStyle: action,
            // setSubtitlesHighlightStyle: action,
            addClipIdForLibrettoClip: action,
            removeClipIdForLibrettoClip: action,
            addLibrettoClipIdToLayer: action,
            removeLibrettoClipIdFromLayer: action,
            display: observable,
            layersOrder: observable
        });
    }

    setUndoRedoState(canUndo: boolean, canRedo: boolean) {
        this.canUndo = canUndo;
        this.canRedo = canRedo;
    }

    getFilenameByClipId(clipId: string) {
        const mediaDataId = CompositionStore.clips[clipId]?.mediaDataId;

        if (mediaDataId == null) {
            return undefined;
        }

        return CompositionStore.media[mediaDataId]?.filename;
    }

    getLibrettoClipByClipId(clipId: string): string | null {
        for (const [librettoClipId, clipIdsSet] of Object.entries(this.librettoClipToRendleyClips)) {
            if (clipIdsSet.has(clipId)) {
                return librettoClipId;
            }
        }
        return null;
    }

    getThumbnailByClipId(clipId: string) {
        const mediaDataId = CompositionStore.clips[clipId]?.mediaDataId;

        if (mediaDataId == null) {
            return undefined;
        }

        return CompositionStore.media[mediaDataId]?.thumbnail;
    }

    getTextByClipId(clipId: string) {
        return CompositionStore.clips[clipId]?.text;
    }

    setDuration(duration: number) {
        this.duration = duration;
    }

    // getSubtitlesMainTextStyle() {
    //     return this.subtitlesMainTextStyle;
    // }
    //
    // getSubtitlesHighlightStyle() {
    //     return this.subtitlesHighlightStyle;
    // }

    updateTimelineDuration() {
        // 10 second offset
        const duration = Math.max(TIMELINE_MIN_DURATION, CompositionService.getFitDuration() + 10);
        this.setDuration(duration);
    }

    resetRender() {
        this.render = {
            error: undefined,
            blobUrl: undefined,
        };
    }

    updateRender(payload: Partial<CompositionStoreInstance["render"]>) {
        this.render = {
            ...this.render,
            ...payload,
        };
    }

    getLayerFromClipId(clipId: string) {
        const layerId = this.layersOrder.find((layerId) => this.layers[layerId].clipsIds.includes(clipId));

        if (layerId == null) {
            return null;
        }

        return this.layers[layerId];
    }

    getLayerFromTransitionId(transitionId: string) {
        const layerId = this.layersOrder.find((layerId) => this.layers[layerId].transitionIds.includes(transitionId));

        if (layerId == null) {
            return null;
        }

        return this.layers[layerId];
    }

    getClipIdsForLibrettoClip(librettoClipId: string): string[] {
        const clipIdsSet = this.librettoClipToRendleyClips[librettoClipId];
        return clipIdsSet ? Array.from(clipIdsSet) : [];
    }

    addClipIdForLibrettoClip(librettoClipId: string, clipId: string) {
        if (!this.librettoClipToRendleyClips[librettoClipId]) {
            this.librettoClipToRendleyClips[librettoClipId] = new Set();
        }
        this.librettoClipToRendleyClips[librettoClipId].add(clipId);
    }

    removeClipIdForLibrettoClip(librettoClipId: string, clipId: string) {
        if (!this.librettoClipToRendleyClips[librettoClipId]) {
            return;
        }
        this.librettoClipToRendleyClips[librettoClipId].delete(clipId);
    }

    getLayerIdForLibrettoClip(librettoClipId: string) {
        return this.librettoClipToLayer[librettoClipId] ?? "";
    }

    setLayerIdForLibrettoClip(librettoClipId: string, layerId: string) {
        this.librettoClipToLayer[librettoClipId] = layerId;
    }

    removeLayerIdForLibrettoClip(librettoClipId: string) {
        this.librettoClipToLayer[librettoClipId] = null;
    }

    getLibrettoClipIdsForLayer(layerId: string): string[] {
        const librettoClipIdsSet = this.layerToLibrettoClips[layerId];
        return librettoClipIdsSet ? Array.from(librettoClipIdsSet) : [];
    }

    addLibrettoClipIdToLayer(librettoClipId: string, layerId: string) {
        if (!this.layerToLibrettoClips[layerId]) {
            this.layerToLibrettoClips[layerId] = new Set();
        }
        this.layerToLibrettoClips[layerId].add(librettoClipId);
    }

    removeLibrettoClipIdFromLayer(librettoClipId: string, layerId: string) {
        if (this.layerToLibrettoClips[layerId]) {
            this.layerToLibrettoClips[layerId].delete(librettoClipId);

            // Clean up empty sets
            if (this.layerToLibrettoClips[layerId].size === 0) {
                delete this.layerToLibrettoClips[layerId];
            }
        }
    }

    setDisplayResolution(width: number, height: number) {
        this.display.width = width;
        this.display.height = height;
    }

    setDisplayBackgroundColor(backgroundColor: string) {
        this.display.backgroundColor = backgroundColor;
    }

    setIsPlaying(isPlaying: boolean) {
        this.isPlaying = isPlaying;
    }

    setCurrentTime(currentTime: number) {
        this.currentTime = currentTime;
    }

    getNumLayers() {
        return this.layersOrder.length;
    }

    setScriptLayerId(layerId: string) {
        this.scriptLayerId = layerId;
    }

    addLayer(layer: Layer) {
        this.layers[layer.id] = layer;
    }

    deleteLayer(layerId: string) {
        const layer = this.layers[layerId];

        if (layer == null) {
            return;
        }

        this.layersOrder = this.layersOrder.filter((l) => l !== layerId);

        delete this.layers[layerId];

        if (this.scriptLayerId === layerId) {
            this.scriptLayerId = null;
        }
    }

    setClip(clip: Clip) {
        this.clips[clip.id] = clip;
    }

    setClips(payload: CompositionStoreInstance["clips"]) {
        this.clips = payload;
    }

    updateClip(clipId: string, payload: Record<string, any>) {
        this.clips[clipId] = {
            ...this.clips[clipId],
            ...payload,
        };
    }

    updateLayer(layerId: string, payload: Record<string, any>) {
        this.layers[layerId] = {
            ...this.layers[layerId],
            ...payload,
        };
    }

    updateStyles(clipId: string, styles: Record<string, any>) {
        this.styles[clipId] = {
            ...(this.styles[clipId] ?? {}),
            ...styles,
        };
    }

    setStyles(styles: Record<string, any>) {
        this.styles = styles;
    }

    addClip(clip: Clip) {
        this.clips[clip.id] = clip;
    }

    deleteClip(clipId: string, layerId: string) {

        if (this.layers[layerId]) {
            this.layers[layerId].clipsIds = this.layers[layerId].clipsIds.filter((id) => id !== clipId);
        }
        delete this.clips[clipId];
    }

    // setMedia(payload: RendleyStoreInstance["media"]) {
    //     this.media = payload;
    // }
    //
    // setLayers(payload: RendleyStoreInstance["layers"]) {
    //     this.layers = payload;
    // }

    addMedia(mediaData: MediaData) {
        this.media[mediaData.id] = mediaData;
    }

    addWaveform(waveformData: WaveformData) {
        this.waveform[waveformData.id] = waveformData;
    }

    deleteMedia(mediaId: string) {
        delete this.media[mediaId];
    }

    deleteWaveform(mediaId: string) {
        delete this.waveform[mediaId];
    }

    setLayersOrder(layersIds: string[]) {
        this.layersOrder = layersIds;
    }

    addTransition(transition: Transition) {
        this.transitions[transition.id] = transition;
    }

    setTransitions(payload: CompositionStoreInstance["transitions"]) {
        this.transitions = payload;
    }

    deleteTransition(transitionId: string, layerId: string) {
        this.layers[layerId] = {
            ...this.layers[layerId],
            transitionIds: this.layers[layerId].transitionIds.filter((t) => t !== transitionId),
        };

        delete this.transitions[transitionId];
    }

    setUpdateTimestamp(timestamp: number) {
        this.updateTimestamp = timestamp;
    }

    setSubtitlesMode(value: string) {
        this.subtitlesStyles.selectedMode = value;
    }

    setSubtitlesAnimation(value: string | null) {
        this.subtitlesStyles.selectedAnimation = value;
    }

    setSubtitlesStyle(value: string | null) {
        this.subtitlesStyles.selectedStyle = value;
    }

    // setSubtitlesHighlightStyle(payload: Partial<HighlightTextStyle>) {
    //     this.subtitlesHighlightStyle = payload;
    // }
    //
    // setSubtitlesMainTextStyle(payload: Partial<MainTextStyle>) {
    //     this.subtitlesMainTextStyle = payload;
    // }

    reset() {
        this.isPlaying = false;
        this.currentTime = 0;
        this.duration = TIMELINE_MIN_DURATION;
        this.display = {
            width: 1920,
            height: 1080,
            backgroundColor: "#000000",
        };
        this.media = {};
        this.waveform = {};
        this.librettoClipToRendleyClips = {};
        this.librettoClipToLayer = {};
        this.layerToLibrettoClips = {};
        this.clips = {};
        this.layers = {};
        this.transitions = {};
        this.layersOrder = [];
        this.styles = {};
        this.updateTimestamp = 0;
        this.render = {
            blobUrl: undefined,
            error: undefined,
            extension: "mp4",
        };
        this.subtitlesStyles = {
            selectedMode: "full",
            selectedAnimation: null,
            selectedStyle: null,
        };
        this.scriptLayerId = null;
    }

    getLibrettoClipState(librettoClipId: string) {
        const clipIds = Array.from(this.librettoClipToRendleyClips[librettoClipId] || []);
        const clips = clipIds.map(id => this.clips[id]).filter(Boolean);

        if (clips.length === 0) {
            return {
                clipIds: [],
                clips: [],
                combinedProperties: null
            };
        }

        // Calculate combined properties
        const combinedProperties = this.calculateCombinedProperties(clips, clipIds, librettoClipId);

        return {
            clipIds,
            clips,
            combinedProperties
        };
    }

    private calculateCombinedProperties(clips: Clip[], clipIds: string[], librettoClipId: string) {
        // Sort clips by startTime to get proper first/last
        const sortedClips = clips
            .map((clip, index) => ({ clip, clipId: clipIds[index] }))
            .sort((a, b) => a.clip.startTime - b.clip.startTime);

        const firstClip = sortedClips[0].clip;
        const lastClip = sortedClips[sortedClips.length - 1].clip;

        // Use stored LibrettoClip start time, fallback to first clip's start time
        const startTime = firstClip.startTime;
        const duration = firstClip.duration; // Duration of the first clip
        const leftTrim = firstClip.leftTrim;
        const rightTrim = lastClip.rightTrim;

        // Total trimmed duration = sum of all individual trimmed durations
        const trimmedDuration = clips.reduce((acc, clip) => acc + clip.trimmedDuration, 0);

        const hasPredecessor = firstClip.hasPredecessor ?? false;
        const hasSuccessor = lastClip.hasSuccessor ?? false;

        return {
            startTime,
            duration,
            leftTrim,
            rightTrim,
            trimmedDuration,
            hasPredecessor,
            hasSuccessor,
            firstClipId: sortedClips[0].clipId,
            lastClipId: sortedClips[sortedClips.length - 1].clipId
        };
    }
}

const CompositionStore = new CompositionStoreInstance();

export { CompositionStore };
